import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRegisterDemoUserMutation } from '../queries';
import { Link } from 'react-router-dom';
import { trackDemoEmailSubmitted } from '../analytics';

interface DemoRegistrationDialogProps {
  open: boolean;
  onClose?: () => void;
  onSuccess: (email: string) => void;
}

interface DemoFormData {
  email: string;
  gdprConsent: boolean;
}

enum SubscriptionDemoState {
  NOT_BOUGHT = 'NOT_BOUGHT',
  ACTIVE = 'ACTIVE',
  CANCELLED = 'CANCELLED',
}

const DemoRegistrationDialog = ({ open, onSuccess }: DemoRegistrationDialogProps) => {
  const intl = useIntl();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<DemoFormData>({
    defaultValues: {
      email: '',
      gdprConsent: false,
    },
  });

  const { mutate: registerDemoUser, isPending } = useRegisterDemoUserMutation();

  const onSubmit = async (data: DemoFormData) => {
    registerDemoUser(
      {
        email: data.email,
        gdprConsent: data.gdprConsent,
        subscriptionState: SubscriptionDemoState.NOT_BOUGHT,
      },
      {
        onSuccess: () => {
          trackDemoEmailSubmitted(data.email);
          onSuccess(data.email);
        },
      },
    );
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          <FormattedMessage
            id="demoRegistration.title"
            defaultMessage="Vyzkoušejte Kadeřník+ na připraveném demo salonu"
          />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              <FormattedMessage
                id="demoRegistration.description"
                defaultMessage="Zadejte svůj e-mail a otevře se vám připravený ukázkový salon, ve kterém si můžete aplikaci projít s reálnými daty."
              />
            </Typography>

            <Controller
              name="email"
              control={control}
              rules={{
                required: intl.formatMessage({
                  id: 'demoRegistration.emailRequired',
                  defaultMessage: 'Email je povinný',
                }),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: intl.formatMessage({
                    id: 'demoRegistration.emailInvalid',
                    defaultMessage: 'Neplatný email',
                  }),
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  autoFocus
                  fullWidth
                  type="email"
                  label={intl.formatMessage({
                    id: 'demoRegistration.emailLabel',
                    defaultMessage: 'Email',
                  })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  sx={{ mb: 2 }}
                />
              )}
            />

            <Controller
              name="gdprConsent"
              control={control}
              rules={{
                required: intl.formatMessage({
                  id: 'demoRegistration.gdprRequired',
                  defaultMessage: 'Musíte souhlasit se zpracováním osobních údajů',
                }),
              }}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} />}
                  label={
                    <Typography variant="body2" color={errors.gdprConsent ? 'error' : 'inherit'}>
                      <FormattedMessage
                        id="demoRegistration.gdprConsent"
                        defaultMessage="Souhlasím se zpracováním e-mailu za účelem zpřístupnění demo verze aplikace Kadeřník+  
a se zasíláním tipů, novinek a praktických informací ke zjednodušení práce v salonu e-mailem."
                      />
                      {' ('}
                      <Link
                        to="https://www.kadernikplus.cz/bezpecnostni-zasady"
                        target="_blank"
                        rel="noopener"
                        style={{
                          color: errors.gdprConsent ? 'inherit' : undefined,
                          textDecoration: 'underline',
                        }}
                      >
                        <FormattedMessage
                          id="demoRegistration.gdprLink"
                          defaultMessage="Zásady ochrany osobních údajů"
                        />
                      </Link>
                      {') '}
                      {errors.gdprConsent && ` - ${errors.gdprConsent.message}`}
                    </Typography>
                  }
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button type="submit" variant="contained" disabled={isPending}>
            <FormattedMessage id="demoRegistration.submit" defaultMessage="Pokračovat" />
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DemoRegistrationDialog;
